import {
  FixReconciliationEmployee,
  CashTransaction,
  AttendanceCashReconciliationResponse,
  CashReconciliationReportRow,
  ReconciliationGridEmployee,
  CashReconciliationReportResponse,
  ReconciliationGridEntry
} from '../interfaces/cash-reconciliation.interface';
import { formatTime } from '../helpers/date.helper';
import { formatDateSafe, formatYmdToPretty } from '../helpers/date.helper';
import {
  mapBadgeToKind,
  recomputeEntry,
  getShiftLabel,
  normalizeDeltaStatus
} from '../helpers/reconciliation.helper';

export function transformFixResponse(apiData: any[]): FixReconciliationEmployee[] {
  return apiData.map(emp => ({
    employee_id: emp.employee_id,
    employee_name: emp.attendant || '—',

    entries: (emp.rows || []).map((r: any) => ({
      id: r.id,
      date_label: r.date,
      shift: r.shift || '--',
      status: r.status,
      status_key: r.status_key || 'pending',
      location: r.location || '—',
      payment_received: r.payment_received,
      opening_balance: r.opening_balance,
      expected_closing: r.expected_closing,
      closing_balance: r.closing_balance,
      extra_cash_total: r.extra_cash_total,
      opening_status: r.opening_status,
      delta: r.delta,
      is_editable: r.is_editable ?? true
    }))
  }));
}

export function transformReportResponse(
  apiData: any[],
  startDate: string,
  endDate: string
): CashReconciliationReportResponse {
  const rows: CashReconciliationReportRow[] = [];
  let totalMatched = 0;
  let totalNotMatched = 0;
  let sumOfDeltas = 0;

  apiData.forEach(emp => {
    (emp.rows || []).forEach((r: any) => {
      const matchStatus = normalizeDeltaStatus(r.match_status);
      const isMatched = matchStatus === 'matched';
      const isUnmatched = matchStatus === 'unmatched';

      // only matched/unmatched should have delta
      const delta =
        isMatched || isUnmatched
          ? (r.delta ?? 0)
          : null;

      // counts
      if (isMatched) {
        totalMatched++;
      }
      if (isUnmatched) {
        totalNotMatched++;
      }
      // sum only valid deltas
      if (delta != null) {
        sumOfDeltas += delta;
      }

      rows.push({
        id: r.id,
        date: formatDateSafe(r.date),
        attendant: r.attendant,
        status: r.match_status,
        status_key: matchStatus,
        shift: r.shift || '--',
        location: r.location || '—',
        opening_balance: r.opening_balance,
        expected_closing: r.expected_closing,
        closing_balance: r.closing_balance,
        extra_cash_total: r.extra_cash_total,
        payment_received: r.payment_received,
        delta,
        match_status: matchStatus
      });
    });
  });

  return {
    rows,
    total_entries: rows.length,
    total_matched: totalMatched,
    total_not_matched: totalNotMatched,
    sum_of_deltas: sumOfDeltas,
    location: rows[0]?.location || '—',
    date_range_label: `${formatYmdToPretty(startDate)} – ${formatYmdToPretty(endDate)}`
  };
}

export function formatCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined || !isFinite(Number(n))) {
    return '0.00';
  }

  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function mapTransactionsResponse(res: any): CashTransaction[] {
  const transactions = res?.data?.transactions ?? [];

  return transactions.map((t: any) => ({
    time: formatTime(t.time),
    orderId: t.order_display_id,
    washType: t.wash_type,
    amount: t.payment_received,
    kind: mapBadgeToKind(t.badge)
  }));
}

export function mapFixDataResponse(data: any[]) {
  return data.map(emp => ({
    employee_id: emp.employee_id,
    employee_name: emp.employee_name,

    entries: emp.entries.map((e: any) =>
      recomputeEntry({
        ...e,
        shift: e.shift?.name || '--'
      })
    )
  }));
}

export function mapReportToGrid(rows: CashReconciliationReportRow[]) {
  const map = new Map<string, ReconciliationGridEmployee>();
  rows.forEach(r => {
    if (!map.has(r.attendant)) {
      map.set(r.attendant, {
        employee_id: r.attendant,
        employee_name: r.attendant,
        entries: []
      });
    }

    map.get(r.attendant)!.entries.push({
      id: r.id,
      date_label: r.date,
      status: r.match_status,
      status_key: normalizeDeltaStatus(r.match_status),
      shift: getShiftLabel(r.shift),
      extra_cash_total: r.extra_cash_total ?? 0,
      location: r.location,
      opening_balance: r.opening_balance,
      expected_closing: r.expected_closing,
      closing_balance: r.closing_balance,
      delta: r.delta,
      match_status: normalizeDeltaStatus(r.match_status),
    } as ReconciliationGridEntry);
  });

  return Array.from(map.values());
}

export function mapAttendanceCashResponse(
  res: any
): AttendanceCashReconciliationResponse {
  const data = res?.data || {};
  return {
    employeeId: data.employee_id ?? null,
    reconciliationId: data.reconciliation_id ?? null,
    employeeName: data.employee_name ?? null,
    openingBalance: Number(data.opening_balance ?? 0),
    paymentReceived: Number(data.payment_received ?? 0),
    expectedClosing: Number(data.expected_closing ?? 0),
    closingBalanceSubmitted: Number(data.closing_balance ?? 0),
    matchStatus: normalizeDeltaStatus(data.match_status),
    shift: getShiftLabel(data.shift) ?? null,
    delta: data.delta != null ? Number(data.delta) : null,
  };
}